import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';

@Injectable({
  providedIn: 'root'
})
export class AndroidIntentService {
  private pendingIntentData: any = null;

  constructor(private platform: Platform) {
    this.setupAndroidShareListener();
  }

  private setupAndroidShareListener() {
    if (this.platform.is('android') && this.platform.is('capacitor')) {
      console.log('[INFO] AndroidIntentService: Setting up androidShare event listener');
      
      // Check if there's already an androidShare event in the queue
      this.checkForExistingAndroidShare();
      
      window.addEventListener('androidShare', (event: any) => {
        console.log('[INFO] AndroidIntentService: Received androidShare event:', event.detail);
        this.pendingIntentData = {
          action: 'android.intent.action.SEND',
          extras: {}
        };

        const detail = event.detail;
        console.log('[INFO] AndroidIntentService: Processing detail - kind:', detail.kind, 'text:', detail.text, 'uri:', detail.uri, 'mimeType:', detail.mimeType, 'androidTimestamp:', detail.androidTimestamp);
        
        // Store Android timestamp for later use
        if (detail.androidTimestamp) {
          this.pendingIntentData.extras['android.intent.extra.ANDROID_TIMESTAMP'] = detail.androidTimestamp;
          console.log('[INFO] AndroidIntentService: Stored Android timestamp:', detail.androidTimestamp, '(', new Date(detail.androidTimestamp).toISOString(), ')');
        }

        if (detail.kind === 'text' && detail.text) {
          this.pendingIntentData.extras['android.intent.extra.TEXT'] = detail.text;
          this.pendingIntentData.extras['android.intent.extra.MIME_TYPE'] = detail.mimeType || 'text/plain';
          console.log('[INFO] AndroidIntentService: Set text content:', detail.text);
        } else if (detail.kind === 'file' && detail.uri) {
          this.pendingIntentData.extras['android.intent.extra.STREAM'] = detail.uri;
          this.pendingIntentData.extras['android.intent.extra.FILENAME'] = detail.text || 'unknown_file';
          this.pendingIntentData.extras['android.intent.extra.MIME_TYPE'] = detail.mimeType || 'application/octet-stream';
          console.log('[INFO] AndroidIntentService: Set file content - uri:', detail.uri, 'filename:', detail.text, 'mimeType:', detail.mimeType);
        } else if (detail.kind === 'files' && detail.uri) {
          this.pendingIntentData.action = 'android.intent.action.SEND_MULTIPLE';
          this.pendingIntentData.extras['android.intent.extra.STREAM'] = [detail.uri];
          this.pendingIntentData.extras['android.intent.extra.FILENAME'] = detail.text || 'unknown_file';
          this.pendingIntentData.extras['android.intent.extra.MIME_TYPE'] = detail.mimeType || 'application/octet-stream';
          console.log('[INFO] AndroidIntentService: Set multiple files content - uri:', detail.uri, 'filename:', detail.text, 'mimeType:', detail.mimeType);
        }
      });
    }
  }

  private checkForExistingAndroidShare() {
    // Check if there's already an androidShare event that was fired before our listener was set up
    // This is a workaround for timing issues

    // ??Check immediately
    this.checkPendingShareData();

    // ??Check again after 500ms (in case native code injects data late)
    setTimeout(() => {
      this.checkPendingShareData();
    }, 500);

    // ??Check again after 1000ms (for very slow app starts)
    setTimeout(() => {
      this.checkPendingShareData();
    }, 1000);
  }

  private checkPendingShareData() {
    if (!this.pendingIntentData) {
      console.log('[INFO] AndroidIntentService: Checking for existing androidShare data...');

      // Check if native code injected pending share data
      const pendingData = (window as any).__pendingAndroidShareData;
      if (pendingData) {
        console.log('[INFO] AndroidIntentService: Found pending share data from native:', pendingData);
        try {
          const detail = pendingData;

          // Process the data as if it came from the event
          this.pendingIntentData = {
            action: 'android.intent.action.SEND',
            extras: {}
          };

          if (detail.androidTimestamp) {
            this.pendingIntentData.extras['android.intent.extra.ANDROID_TIMESTAMP'] = detail.androidTimestamp;
            console.log('[INFO] AndroidIntentService: Stored Android timestamp:', detail.androidTimestamp);
          }

          if (detail.kind === 'text' && detail.text) {
            this.pendingIntentData.extras['android.intent.extra.TEXT'] = detail.text;
            this.pendingIntentData.extras['android.intent.extra.MIME_TYPE'] = detail.mimeType || 'text/plain';
            console.log('[INFO] AndroidIntentService: Set text content from pending data');
          } else if (detail.kind === 'file' && detail.uri) {
            this.pendingIntentData.extras['android.intent.extra.STREAM'] = detail.uri;
            this.pendingIntentData.extras['android.intent.extra.FILENAME'] = detail.text || 'unknown_file';
            this.pendingIntentData.extras['android.intent.extra.MIME_TYPE'] = detail.mimeType || 'application/octet-stream';
            console.log('[INFO] AndroidIntentService: Set file content from pending data');
          } else if (detail.kind === 'files' && detail.uri) {
            this.pendingIntentData.action = 'android.intent.action.SEND_MULTIPLE';
            this.pendingIntentData.extras['android.intent.extra.STREAM'] = [detail.uri];
            this.pendingIntentData.extras['android.intent.extra.FILENAME'] = detail.text || 'unknown_file';
            this.pendingIntentData.extras['android.intent.extra.MIME_TYPE'] = detail.mimeType || 'application/octet-stream';
            console.log('[INFO] AndroidIntentService: Set multiple files content from pending data');
          }

          // Clear the pending data
          delete (window as any).__pendingAndroidShareData;

          // ??Trigger a manual check in IntentHandlerService
          console.log('[INFO] AndroidIntentService: Triggering manual share check');
          window.dispatchEvent(new CustomEvent('androidShare', { detail: detail }));
        } catch (error) {
          console.error('[INFO] AndroidIntentService: Error processing pending share data:', error);
        }
      } else {
        console.log('[INFO] AndroidIntentService: No pending share data found in window object');
      }
    }
  }

  async getIntentData(): Promise<any> {
    console.log('[INFO] AndroidIntentService: Checking for intent data');
    console.log('[INFO] Platform is Android:', this.platform.is('android'));
    console.log('[INFO] Platform is Capacitor:', this.platform.is('capacitor'));

    if (this.platform.is('android') && this.platform.is('capacitor')) {
      try {
        // Method 1: Check for pending androidShare event data
        if (this.pendingIntentData) {
          console.log('[INFO] Found pending androidShare data:', this.pendingIntentData);
          const data = this.pendingIntentData;
          this.pendingIntentData = null;
          return data;
        }

        // Method 2: Try Capacitor App plugin
        console.log('[INFO] Trying Capacitor App.getLaunchUrl...');
        const launchUrl = await App.getLaunchUrl();
        if (launchUrl && launchUrl.url) {
          console.log('[INFO] Found launch URL:', launchUrl.url);
          return this.parseLaunchUrl(launchUrl.url);
        }

        // Capacitor-first: no Cordova intent fallback
        console.log('[INFO] No pending share data found via Capacitor APIs');
        return null;
      } catch (error) {
        console.error('[INFO] Error getting intent data:', error);
        return null;
      }
    }

    return null;
  }

  private parseLaunchUrl(url: string): any {
    console.log('[INFO] Parsing launch URL:', url);
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      
      // Check for shared content in URL parameters
      const text = params.get('text');
      const file = params.get('file');
      
      if (text) {
        return {
          action: 'android.intent.action.SEND',
          extras: {
            'android.intent.extra.TEXT': text
          }
        };
      }
      
      if (file) {
        return {
          action: 'android.intent.action.SEND',
          extras: {
            'android.intent.extra.STREAM': file
          }
        };
      }
      
      return null;
    } catch (error) {
      console.error('[INFO] Error parsing launch URL:', error);
      return null;
    }
  }

  async checkForSharedContent(): Promise<any> {
    try {
      console.log('[INFO] checkForSharedContent: Starting check');
      const intent = await this.getIntentData();
      console.log('[INFO] checkForSharedContent: Intent data:', intent);
      
      if (intent && intent.action === 'android.intent.action.SEND') {
        console.log('[INFO] checkForSharedContent: Found SEND intent');
        const sharedData: any = {};
        
        // Store Android timestamp if available
        if (intent.extras && intent.extras['android.intent.extra.ANDROID_TIMESTAMP']) {
          sharedData.androidTimestamp = intent.extras['android.intent.extra.ANDROID_TIMESTAMP'];
          console.log('[INFO] checkForSharedContent: Found Android timestamp:', sharedData.androidTimestamp, '(', new Date(sharedData.androidTimestamp).toISOString(), ')');
        }

        // Handle text sharing
        if (intent.extras && intent.extras['android.intent.extra.TEXT']) {
          console.log('[INFO] checkForSharedContent: Found text content');
          sharedData.type = 'text';
          sharedData.content = intent.extras['android.intent.extra.TEXT'];
          sharedData.mimeType = intent.extras['android.intent.extra.MIME_TYPE'] || 'text/plain';
        }
        
        // Handle file sharing
        if (intent.extras && intent.extras['android.intent.extra.STREAM']) {
          console.log('[INFO] checkForSharedContent: Found file content');
          sharedData.type = 'file';
          sharedData.content = intent.extras['android.intent.extra.STREAM'];
          sharedData.filename = intent.extras['android.intent.extra.FILENAME'] || 'unknown_file';
          sharedData.mimeType = intent.extras['android.intent.extra.MIME_TYPE'] || 'application/octet-stream';
          console.log('[INFO] checkForSharedContent: File details - content:', sharedData.content, 'filename:', sharedData.filename, 'mimeType:', sharedData.mimeType);
        }
        
        // Handle multiple files
        if (intent.action === 'android.intent.action.SEND_MULTIPLE' && intent.extras && intent.extras['android.intent.extra.STREAM']) {
          console.log('[INFO] checkForSharedContent: Found multiple files content');
          sharedData.type = 'multiple_files';
          sharedData.content = intent.extras['android.intent.extra.STREAM'];
          sharedData.filename = intent.extras['android.intent.extra.FILENAME'] || 'unknown_file';
          sharedData.mimeType = intent.extras['android.intent.extra.MIME_TYPE'] || 'application/octet-stream';
          console.log('[INFO] checkForSharedContent: Multiple files details - content:', sharedData.content, 'filename:', sharedData.filename, 'mimeType:', sharedData.mimeType);
        }
        
        console.log('[INFO] checkForSharedContent: Returning shared data:', sharedData);
        return sharedData;
      }
      
      console.log('[INFO] checkForSharedContent: No valid intent found');
      return null;
    } catch (error) {
      console.error('[INFO] checkForSharedContent: Error checking for shared content:', error);
      return null;
    }
  }

}



